import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import InvoiceDetailsElement from "modules/billing/components/InvoiceDetailsElement";
import { useMemo } from "react";
import { generatePath, NavLink } from "react-router-dom";

import CompanyLogo from "~/components/CompanyLogo";
import Hint from "~/components/Hint";
import { PROJECT_DETAILS_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import { getInvoiceTypeDisplayNameById, invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import type { StaffingClosingStatusesByMonth } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import UsersMonthlyClosingStatus from "~/modules/billing/components/UsersMonthlyClosingStatus";
import { ProjectDetailsTabName } from "~/modules/project/types/projectViewTypes.ts";
import type { User, UserWithConfidentialInformation } from "~/modules/user/api/user/userTypes.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

type Props = {
	allUsersData: User[] | UserWithConfidentialInformation[];
	children?: React.ReactNode;
	invoiceData: UnprocessedInvoicesIndexTableData,
	showClosingStatusError?: boolean;
	staffingsClosingStatuses: StaffingClosingStatusesByMonth[];
};

const InvoiceLink = ({ phaseNumber, phaseTitle, projectId }: {
	projectId: string,
	phaseNumber: number
	phaseTitle: string;
}) => {
	return <NavLink to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
		projectId: projectId,
		tabId: "phase-" + phaseNumber,
	})}
					className="hover:text-secondary-500">{phaseTitle}</NavLink>;
};

// children prop is only meant to be used to show the delivery period form elements
const CommonProjectInvoiceDetails = ({
	allUsersData,
	children,
	invoiceData,
	showClosingStatusError = false,
	staffingsClosingStatuses,
}: Props) => {
	const orderManagerFullName = invoiceData.orderManagedBy ? allUsersData.find(user => user.id === invoiceData.orderManagedBy)?.fullName : null;
	const phaseTitles = useMemo(() => {
		const phasesSorted = invoiceData.phases.sort(byObjectProperty("phaseNumber", "desc"));
		const primaryPhaseData = phasesSorted[0];
		if (invoiceData.phases.length === 1) {
			return <InvoiceLink phaseTitle={primaryPhaseData.phaseTitle}
								phaseNumber={primaryPhaseData.phaseNumber}
								projectId={invoiceData.projectId!} />;
		}
		return "diverse";
	}, [invoiceData.phases, invoiceData.projectId]);

	const phaseSectionTitle = invoiceData.phases.length > 1 ? "Phasen" : "Phase";

	let orderTitle = "diverse";
	if (invoiceData.orders.length === 1) {
		const order = invoiceData.orders[0];
		orderTitle = order.title;
	}

	const isAdvanceInvoice = invoiceData.invoiceTypeId === invoiceTypeId.AdvanceInvoice;

	return <dl className="divide-y divide-gray-100">
		<InvoiceDetailsElement label="Kunde">
			<div className="flex items-center gap-x-2">
				{invoiceData.companyAvatarImage &&
					<CompanyLogo image={invoiceData.companyAvatarImage}
								 sizeClassName="size-6" />}
				<div className="truncate">{invoiceData.companyDisplayName}</div>
			</div>
		</InvoiceDetailsElement>
		<InvoiceDetailsElement label="Projekt">
			<NavLink to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
				projectId: invoiceData.projectId,
				tabId: ProjectDetailsTabName.Invoices,
			})}
					 className="hover:text-secondary-500">{invoiceData.projectTitle}</NavLink>
		</InvoiceDetailsElement>
		<InvoiceDetailsElement label={phaseSectionTitle}>
			{phaseTitles}
		</InvoiceDetailsElement>
		{!isAdvanceInvoice && <InvoiceDetailsElement label="Bestellung">
			{orderTitle}
		</InvoiceDetailsElement>}
		<InvoiceDetailsElement label="Manager">
			{orderManagerFullName || EMPTY_VALUE_PLACEHOLDER}
		</InvoiceDetailsElement>
		{children ? children : <>
			<InvoiceDetailsElement label="Abrechnungsart">{getInvoiceTypeDisplayNameById(invoiceData.invoiceTypeId)}</InvoiceDetailsElement>
			{invoiceData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice &&
				<InvoiceDetailsElement label="Rechnungsnummer Kundengutschrift">{invoiceData.invoiceNumber}</InvoiceDetailsElement>}
			<InvoiceDetailsElement label="Leistungszeitraum">
				{formatSmartDateRange(invoiceData.deliveryStartDate, invoiceData.deliveryEndDate, {
					month: "MMMM",
					year: " yyyy",
				})}
			</InvoiceDetailsElement>
		</>}
		<InvoiceDetailsElement label="Rechnungssumme (netto)">
			{formatCentsToCurrency(invoiceData.invoiceTotalCents)}
		</InvoiceDetailsElement>
		{!isAdvanceInvoice && <><InvoiceDetailsElement label="Freelancer">
			{invoiceData.hasFreelancers ? <CheckCircleIcon className="size-5 fill-success-600" /> :
				<XCircleIcon className="size-5 fill-red-500" />}

		</InvoiceDetailsElement>
			<InvoiceDetailsElement label="Monatsabschluss"
								   truncate={false}>
				<div className="flex w-full flex-row flex-wrap gap-1">
					<UsersMonthlyClosingStatus allUsersData={allUsersData}
											   staffingsClosingStatuses={staffingsClosingStatuses} />
				</div>
			</InvoiceDetailsElement></>}
		{!isAdvanceInvoice && showClosingStatusError && <div className="w-full"><Hint size="sm"
																					  theme="error">Der
			Abrechnungsvorgang
			kann erst
			gestartet werden, wenn alle Teammitglieder ihre Monatsabschlüsse für den gesamten
			Leistungszeitraum
			getätigt haben.</Hint></div>}
		<InvoiceDetailsElement truncate={false}
							   label="Kommentar Bestellung">
			{nl2br(invoiceData.orderComment) || EMPTY_VALUE_PLACEHOLDER}
		</InvoiceDetailsElement>
		<InvoiceDetailsElement truncate={false}
							   label="Kommentar Rechnungssteller:in">
			{nl2br(invoiceData.comment) || EMPTY_VALUE_PLACEHOLDER}
		</InvoiceDetailsElement>
	</dl>;
};

export default CommonProjectInvoiceDetails;